import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyResetToken, resetPassword } from '../services/authApi';
import './ResetPassword.css';

const ResetPassword = ({ onResetSuccess, onBack }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [tokenValid, setTokenValid] = useState(false);

  // Extract token from URL on mount
  useEffect(() => {
    const urlToken = searchParams.get('token');
    
    if (!urlToken) {
      setMessage({ 
        type: 'error', 
        text: '❌ Invalid or missing reset token. Please check your email for the correct link.' 
      });
      setVerifying(false);
      return;
    }

    setToken(urlToken);
    verifyToken(urlToken);
  }, [searchParams]);

  // Verify token with backend
  const verifyToken = async (resetToken) => {
    try {
      setVerifying(true);
      const response = await verifyResetToken(resetToken);
      
      if (response.valid) {
        setTokenValid(true);
        setMessage({ 
          type: 'info', 
          text: `✅ Welcome ${response.username || 'User'}! Please set your new password.` 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: `❌ ${response.message || 'This reset link is invalid or has expired. Please request a new one.'}` 
        });
      }
    } catch (error) {
      console.error('Token verification error:', error);
      setMessage({ 
        type: 'error', 
        text: '❌ Unable to verify token. Please check your connection.' 
      });
    } finally {
      setVerifying(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (password.length < 6) {
      setMessage({ type: 'error', text: '❌ Password must be at least 6 characters long.' });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: '❌ Passwords do not match.' });
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, password);
      
      setMessage({ 
        type: 'success', 
        text: '✅ Password reset successful! You can now login with your new password.' 
      });
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        if (onResetSuccess) {
          onResetSuccess();
        } else {
          navigate('/login');
        }
      }, 2000);
    } catch (error) {
      console.error('Reset error:', error);
      setMessage({ 
        type: 'error', 
        text: `❌ ${error.message || 'Failed to reset password. Please try again.'}` 
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordsMatch = password === confirmPassword && password.length > 0;
  const passwordLongEnough = password.length >= 6;

  if (verifying) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="loading">
            <div className="spinner"></div> Verifying reset link...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <h1>🔐 Reset Your Password</h1>
        <p className="subtitle">Enter your new password below</p>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {tokenValid && message.type !== 'success' && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="token">Reset Token (Auto-filled)</label>
              <input 
                type="text" 
                id="token" 
                value={token}
                readOnly
                className="readonly-input"
                placeholder="Token from email"
              />
              <small>✓ Token automatically extracted from email link</small>
            </div>

            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input 
                type="password" 
                id="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                minLength="6"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input 
                type="password" 
                id="confirmPassword" 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                minLength="6"
                disabled={loading}
              />
            </div>

            <div className="password-requirements">
              <div className={`requirement ${passwordLongEnough ? 'met' : ''}`}>
                {passwordLongEnough ? '✓' : '○'} Minimum 6 characters
              </div>
              <div className={`requirement ${passwordsMatch ? 'met' : ''}`}>
                {passwordsMatch ? '✓' : '○'} Passwords match
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading || !passwordLongEnough || !passwordsMatch}
              className="submit-btn"
            >
              {loading ? (
                <>
                  <span className="spinner-inline"></span> Processing...
                </>
              ) : (
                '🔓 Reset Password'
              )}
            </button>
          </form>
        )}

        {!tokenValid && !verifying && (
          <div className="error-actions">
            <p>The reset link may have expired or is invalid.</p>
            <button className="back-link" onClick={() => navigate('/login')}>
              ← Back to Login
            </button>
          </div>
        )}

        <div className="link-text">
          <a href="/login">← Back to Login</a>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
